import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Typography
} from "@mui/material";
import { Plus, Pencil, Trash2 } from "lucide-react";
import BreadCrumbs from "../components/Breadcrumbs";
import {
    getShopStore,
    createServerStoreItem,
    editServerStoreItem,
    getServerStoreItems,
    deleteServerStoreItem,
    sortServerStoreItems
} from "../http/ShopAPI";
import { useParams } from "react-router-dom";
import { SERVERS_ROUTE } from "../utils/consts";

/* dnd-kit */
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    rectSortingStrategy,
    arrayMove
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/* ============================
   Sortable Card wrapper
============================ */
const SortableCard = ({ id, children }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: "grab"
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {children}
        </div>
    );
};

/* ============================
   Main component
============================ */
const ShopItems = observer(() => {
    const { serverId } = useParams();

    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [shopItems, setShopItems] = useState([]);
    const [globalItems, setGlobalItems] = useState([]);
    const [globalCategories, setGlobalCategories] = useState([]);
    const [filterCategory, setFilterCategory] = useState("");

    const [form, setForm] = useState({
        is_custom: false,
        item_id: null,
        id: null,
        category_id: null,
        custom_name: "",
        custom_description: "",
        custom_image_url: "",
        command: "",
        price: 0,
        stock: null,
        is_active: true
    });

    const hrefs = [{ href: SERVERS_ROUTE, name: "Список серверов" }];

    /* ============================
       Load data
    ============================ */
    const loadGlobalItems = async () => {
        const { items, categories } = await getShopStore();
        setGlobalItems(items);
        setGlobalCategories(categories);
    };

    const loadServerStoreItems = async () => {
        const data = await getServerStoreItems(serverId);

        // Если sort = 0 или равен id (старые данные), перенумеруем
        const normalized = data
            .sort((a, b) => b.sort - a.sort || b.id - a.id) // DESC, как на бэке
            .map((item, index) => ({
                ...item,
                sort: data.length - index, // максимальный sort → первый элемент
            }));

        setShopItems(normalized);
    };

    useEffect(() => {
        loadGlobalItems();
        loadServerStoreItems();
    }, [serverId]);

    /* ============================
       Drag end handler
    ============================ */
    const handleDragEnd = async ({ active, over }) => {
        if (!over || active.id === over.id) return;

        const oldIndex = shopItems.findIndex(i => i.id === active.id);
        const newIndex = shopItems.findIndex(i => i.id === over.id);

        if (oldIndex === -1 || newIndex === -1) return;

        const moved = arrayMove(shopItems, oldIndex, newIndex);

        // определяем диапазон изменений
        const from = Math.min(oldIndex, newIndex);
        const to = Math.max(oldIndex, newIndex);

        const changed = [];

        // текущий максимум sort
        const maxSort = shopItems.length;

        for (let i = from; i <= to; i++) {
            const newSort = maxSort - i;
            if (moved[i].sort !== newSort) {
                moved[i] = { ...moved[i], sort: newSort };
                changed.push({ id: moved[i].id, sort: newSort });
            }
        }

        // ✅ сначала фиксируем UI
        setShopItems(moved);

        // ✅ потом сохраняем diff
        try {
            if (changed.length) {
                await sortServerStoreItems(serverId, changed);
            }
        } catch (e) {
            console.error("Ошибка сортировки", e);
            await loadServerStoreItems();
        }
    };

    /* ============================
       Helpers
    ============================ */
    const sortedItems = shopItems;

    const filteredGlobalItems = globalItems.filter(item =>
        !filterCategory || item.category_id === Number(filterCategory)
    );

    const handleOpen = (item = null) => {
        if (item) {
            setEditing(item);
            setForm({
                is_custom: !item.item_id,
                item_id: item.item_id || null,
                id: item.id || null,
                category_id: item.category_id || null,
                custom_name: item.custom_name || "",
                custom_description: item.custom_description || "",
                custom_image_url: item.custom_image_url || "",
                command: item.command || "",
                price: item.price,
                stock: item.stock,
                is_active: item.is_active
            });
        } else {
            setEditing(null);
            setForm({
                is_custom: false,
                item_id: null,
                id: null,
                category_id: null,
                custom_name: "",
                custom_description: "",
                custom_image_url: "",
                command: "",
                price: 0,
                stock: null,
                is_active: true
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditing(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        const data = { server_id: serverId, ...form };
        if (editing) {
            await editServerStoreItem(editing.id, data);
        } else {
            await createServerStoreItem(data);
        }
        await loadServerStoreItems();
        handleClose();
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Удалить товар?")) return;
        await deleteServerStoreItem(id);
        await loadServerStoreItems();
    };

    /* ============================
       Render
    ============================ */
    return (
        <Container maxWidth="xl" sx={{ mt: 4 }}>
            <BreadCrumbs hrefs={hrefs} name="Список товаров магазина" />

            <Box display="flex" justifyContent="space-between" mb={2} mt={3}>
                <Typography variant="h5">Управление товарами</Typography>
                <Button onClick={() => handleOpen()}>
                    <Plus size={18} /> Добавить товар
                </Button>
            </Box>

            <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={shopItems.map(i => i.id)}
                    strategy={rectSortingStrategy}
                >
                    <Grid container spacing={2}>
                        {shopItems.map(item => (
                            <Grid item xs={12} sm={6} md={4} key={item.id}>
                                <SortableCard id={item.id}>
                                    <Card>
                                        <CardHeader
                                            title={item.item_id ? `📦 ${item.name}` : `⭐ ${item.custom_name}`}
                                            action={
                                                <>
                                                    <IconButton onClick={() => handleOpen(item)}>
                                                        <Pencil size={18} />
                                                    </IconButton>
                                                    <IconButton onClick={() => handleDelete(item.id)}>
                                                        <Trash2 size={18} />
                                                    </IconButton>
                                                </>
                                            }
                                        />
                                        {item.image_url && (
                                            <Box
                                                component="img"
                                                src={item.image_url}
                                                sx={{
                                                    maxHeight: 100,
                                                    objectFit: "contain",
                                                    width: "100%",
                                                    pointerEvents: "none"
                                                }}
                                            />
                                        )}
                                        <CardContent>
                                            <Typography variant="body2">
                                                Цена: {item.price}
                                            </Typography>
                                            <Typography variant="body2">
                                                {item.item_id
                                                    ? "Из мастер-каталога"
                                                    : "Кастомный товар"}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </SortableCard>
                            </Grid>
                        ))}
                    </Grid>
                </SortableContext>
            </DndContext>

            {/* ===== Dialog ===== */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>
                    {editing ? "Редактировать товар" : "Добавить товар"}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        select
                        fullWidth
                        label="Тип товара"
                        value={form.is_custom ? "custom" : "global"}
                        onChange={(e) =>
                            setForm(prev => ({
                                ...prev,
                                is_custom: e.target.value === "custom",
                                item_id: null
                            }))
                        }
                        margin="normal"
                    >
                        <MenuItem value="global">Из мастер-каталога</MenuItem>
                        <MenuItem value="custom">Кастомный товар</MenuItem>
                    </TextField>

                    {!form.is_custom ? (
                        <>
                            <TextField
                                select
                                fullWidth
                                label="Категория"
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                                margin="normal"
                            >
                                <MenuItem value="">Все категории</MenuItem>
                                {globalCategories.map(cat => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                fullWidth
                                label="Товар"
                                value={form.item_id || ""}
                                onChange={(e) =>
                                    setForm(prev => ({
                                        ...prev,
                                        item_id: Number(e.target.value)
                                    }))
                                }
                                margin="normal"
                            >
                                {filteredGlobalItems.map(item => (
                                    <MenuItem key={item.id} value={item.id}>
                                        {item.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </>
                    ) : (
                        <>
                            <TextField
                                label="Название"
                                name="custom_name"
                                fullWidth
                                value={form.custom_name}
                                onChange={handleChange}
                                margin="normal"
                            />
                            <TextField
                                label="Описание"
                                name="custom_description"
                                fullWidth
                                multiline
                                rows={2}
                                value={form.custom_description}
                                onChange={handleChange}
                                margin="normal"
                            />
                            <TextField
                                label="Изображение (URL)"
                                name="custom_image_url"
                                fullWidth
                                value={form.custom_image_url}
                                onChange={handleChange}
                                margin="normal"
                            />
                            <TextField
                                label="Команда"
                                name="command"
                                fullWidth
                                value={form.command}
                                onChange={handleChange}
                                margin="normal"
                            />
                        </>
                    )}

                    <TextField
                        label="Цена"
                        name="price"
                        type="number"
                        fullWidth
                        value={form.price}
                        onChange={handleChange}
                        margin="normal"
                    />

                    <TextField
                        label="Кол-во"
                        name="stock"
                        type="number"
                        fullWidth
                        value={form.stock || ""}
                        onChange={handleChange}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Отмена</Button>
                    <Button onClick={handleSave}>Сохранить</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
});

export default ShopItems;
